// TelcoTrack — ROZVADEC multi-entry záfuk (primární OK + svazek mikrotrubiček + MT).

import {
  applyDerivedTypOkList,
  isDerivedTypOkValue,
  OK_MATERIAL_REQUIRED_MESSAGE,
  resolveTypOkFromMaterials,
  type OkMaterialUsageLike,
} from './typ-ok-from-material.constants';
import { fiberCountFromTypOk, isAnyTypOkValue } from './typ-ok.constants';

export { fiberCountFromTypOk };
import { parseZafukMetaDouble, recomputeZafukLengthFromOkMetraz } from './zafuk-spec.constants';
import {
  normalizeZafukSourceRef,
  zafukSourceToFlatFields,
  zafukSourceToLegacyFields,
  type ZafukSourceObjectType,
} from './zafuk-source.constants';

/** All known kinds incl. legacy single-MT (still parsed; no longer offered in pickers). */
export const ROZVADEC_ZAFUK_KINDS = ['opticka_vlna', 'mikrotrubicky_svazek', 'mikrotrubicka'] as const;
export type RozvadecZafukKind = (typeof ROZVADEC_ZAFUK_KINDS)[number];

/** Specifikovat / Bod záfuku picker — OK + svazek only (single MT removed). */
export const ROZVADEC_ZAFUK_PICKER_KINDS = ['opticka_vlna', 'mikrotrubicky_svazek'] as const;
export type RozvadecZafukPickerKind = (typeof ROZVADEC_ZAFUK_PICKER_KINDS)[number];

export const ROZVADEC_ZAFUK_KIND_LABELS: Record<RozvadecZafukKind, string> = {
  opticka_vlna: 'Optické vlákno (primární OK)',
  mikrotrubicky_svazek: 'Svazek mikrotrubiček',
  mikrotrubicka: 'Mikrotrubička (MT)',
};

/** Svazek mikrotrubiček i legacy jednotlivá Mikrotrubička (MT) — bez typ OK / optického materiálu. */
export function isRozvadecZafukMicrotubeKind(
  kind: RozvadecZafukKind | string | null | undefined,
): boolean {
  return kind === 'mikrotrubicky_svazek' || kind === 'mikrotrubicka';
}

/** Placeholder in Specifikovat — fail-closed until the user picks OK vs svazek MT. */
export const ROZVADEC_ZAFUK_KIND_UNSET_LABEL = 'Zvolte typ záfuku';

export const ROZVADEC_ZAFUK_KIND_REQUIRED_MESSAGE =
  'Zvolte typ záfuku (OK nebo svazek mikrotrubiček).';

/** ROZVADEC multi-entry záfuk (not BUDOVA scalars; KONCOVYBOD no longer uses this form). */
export function objectTypeUsesRozvadecZafukEntries(type: string | null | undefined): boolean {
  const t = String(type ?? '').trim().toUpperCase();
  return t === 'ROZVADEC';
}

export interface RozvadecZafukEntry {
  /** Client-generated id for list tracking (UUID or short random). */
  id: string;
  /** Fail-closed: unset until the user explicitly picks OK vs svazek / MT. */
  kind: RozvadecZafukKind | null;
  /**
   * Legacy per-row marker. Výchozí bod exists once on the object (`zafukVychoziBod`) —
   * XOR: výchozí bod ≠ záfuk. Individual záfuk rows must not pick a new origin.
   */
  vychoziBod: boolean;
  okMetrazStartM?: number | null;
  okMetrazKonecM?: number | null;
  /** Jen pro opticka_vlna (12–192 vl.). */
  typOk?: string | null;
  sourceRozvadecObjectId?: string | null;
  sourceRozvadecName?: string | null;
  /** Neutrální zdroj (ROZVADEC nebo SPOJKOVISTE). Legacy `sourceRozvadec*` jen pro typ ROZVADEC. */
  zafukSourceObjectId?: string | null;
  zafukSourceObjectType?: ZafukSourceObjectType | null;
  zafukSourceName?: string | null;
}

export interface RozvadecZafukLegacyFields {
  zafukVychoziBod: boolean;
  zafukLengthM: number | null;
  zafukOkMetrazStartM: number | null;
  zafukOkMetrazKonecM: number | null;
  zafukTypOk: string | null;
  zafukZRozvadecObjectId: string | null;
  zafukZRozvadecName: string | null;
}

function randomEntryId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `zafuk-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function isRozvadecZafukKind(value: unknown): value is RozvadecZafukKind {
  return typeof value === 'string' && (ROZVADEC_ZAFUK_KINDS as readonly string[]).includes(value.trim());
}

/** Display label; unset / unknown kind stays fail-closed (never guessed as OK). */
export function rozvadecZafukKindLabel(kind: RozvadecZafukKind | null | undefined): string {
  return isRozvadecZafukKind(kind)
    ? ROZVADEC_ZAFUK_KIND_LABELS[kind]
    : ROZVADEC_ZAFUK_KIND_UNSET_LABEL;
}

export function createEmptyRozvadecZafukEntry(
  kind: RozvadecZafukKind | null = null,
): RozvadecZafukEntry {
  return {
    id: randomEntryId(),
    kind,
    vychoziBod: false,
    okMetrazStartM: null,
    okMetrazKonecM: null,
    typOk: null,
    sourceRozvadecObjectId: null,
    sourceRozvadecName: null,
  };
}

/** Object-level origin label (not a záfuk row). */
export const ROZVADEC_ZAFUK_VYCHOZI_BOD_LABEL = 'Výchozí bod';

export function rozvadecZafukEntriesHaveVychoziBod(
  entries: readonly RozvadecZafukEntry[] | null | undefined,
): boolean {
  return (entries ?? []).some((entry) => entry.vychoziBod === true);
}

/** Collapse leftover per-row flags into one object-level origin (no záfuk metadata). */
export function collapseRozvadecZafukEntriesToObjectVychoziBod(
  entries: readonly RozvadecZafukEntry[] = [],
): RozvadecZafukEntry {
  const template = entries.find((e) => e.vychoziBod) ?? entries[0];
  return {
    id: template?.id || 'legacy-primary',
    kind: 'opticka_vlna',
    vychoziBod: true,
    okMetrazStartM: null,
    okMetrazKonecM: null,
    typOk: null,
    sourceRozvadecObjectId: null,
    sourceRozvadecName: null,
  };
}

/** XOR: object is the route origin — not a záfuk (no length / source / typ OK). */
export function isRozvadecObjectVychoziBod(
  sources: {
    rozvadecZafukEntries?: unknown;
    metadata?: Record<string, unknown> | null;
    zafukVychoziBod?: boolean | null;
  },
): boolean {
  if (sources.zafukVychoziBod === true || sources.metadata?.['zafukVychoziBod'] === true) {
    return true;
  }
  const raw = sources.rozvadecZafukEntries ?? sources.metadata?.['rozvadecZafukEntries'];
  if (!Array.isArray(raw)) return false;
  return raw.some((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
    const o = item as Record<string, unknown>;
    return o['vychoziBod'] === true || o['vychoziBod'] === 'true';
  });
}

export function rozvadecZafukEntryLengthM(entry: RozvadecZafukEntry): number | null {
  return recomputeZafukLengthFromOkMetraz(entry.okMetrazStartM, entry.okMetrazKonecM);
}

/**
 * Součet délek optických záfuků (Informace „Délka záfuku“).
 * Výchozí bod, svazek mikrotrubiček a jednotlivá MT délku nemají — do součtu nejdou.
 */
export function sumRozvadecZafukEntriesLengthM(entries: RozvadecZafukEntry[]): number | null {
  const filtered = filterRozvadecZafukEntriesForWorkflowValidation(entries);
  if (rozvadecZafukEntriesHaveVychoziBod(filtered)) return null;
  let sum = 0;
  let any = false;
  for (const entry of filtered) {
    if (entry.kind !== 'opticka_vlna') continue;
    const lengthM = rozvadecZafukEntryLengthM(entry);
    if (lengthM == null || !Number.isFinite(lengthM) || lengthM < 0) continue;
    sum += lengthM;
    any = true;
  }
  return any ? Math.round(sum * 100) / 100 : null;
}

/** Délka záfuku pro seznam ROZVADEC: součet položek, jinak legacy sloupce. */
export function resolveRozvadecZafukLengthM(
  sources: Parameters<typeof normalizeRozvadecZafukEntries>[0],
): number | null {
  return sumRozvadecZafukEntriesLengthM(normalizeRozvadecZafukEntries(sources));
}

/** Zdroj záfuku řádku: spojkoviště i rozvaděč. Legacy id bez typu = ROZVADEC. */
export function rozvadecZafukEntrySourceId(entry: RozvadecZafukEntry): string | null {
  return normalizeZafukSourceRef(entry)?.objectId ?? null;
}

export function isRozvadecZafukEntryValid(entry: RozvadecZafukEntry): boolean {
  // Object-level origin is not a záfuk row — no metráž / source / typ OK.
  if (entry.vychoziBod) return true;
  if (!isRozvadecZafukKind(entry.kind)) return false;
  if (!rozvadecZafukEntrySourceId(entry)) return false;
  if (isRozvadecZafukMicrotubeKind(entry.kind)) return true;
  const lengthM = rozvadecZafukEntryLengthM(entry);
  if (lengthM == null || !Number.isFinite(lengthM) || lengthM < 0) return false;
  const typOk = (entry.typOk ?? '').trim();
  if (!typOk || !isDerivedTypOkValue(typOk)) return false;
  return true;
}

/** True only when an explicit optical-OK záfuk row requires OK cable material. */
export function rozvadecZafukNeedsOpticalCableMaterial(entries: RozvadecZafukEntry[]): boolean {
  const filtered = filterRozvadecZafukEntriesForWorkflowValidation(entries);
  if (rozvadecZafukEntriesHaveVychoziBod(filtered)) return false;
  return filtered.some((entry) => entry.kind === 'opticka_vlna');
}

/** Fill typ OK on optical záfuk rows from selected / used cable materials. */
export function applyTypOkFromMaterialsToZafukEntries(
  entries: RozvadecZafukEntry[],
  materials: readonly OkMaterialUsageLike[] | null | undefined,
): RozvadecZafukEntry[] {
  const resolved = resolveTypOkFromMaterials(materials);
  const opticalIdx: number[] = [];
  entries.forEach((entry, i) => {
    if (!entry.vychoziBod && entry.kind === 'opticka_vlna') opticalIdx.push(i);
  });
  const assigned = applyDerivedTypOkList(opticalIdx.length, resolved.types);
  return entries.map((entry, i) => {
    const slot = opticalIdx.indexOf(i);
    if (slot < 0) return entry;
    const derived = assigned[slot];
    return { ...entry, typOk: derived ?? (isDerivedTypOkValue(entry.typOk) ? entry.typOk : null) };
  });
}

export function deriveBudovaZafukTypOkFromMaterials(
  materials: readonly OkMaterialUsageLike[] | null | undefined,
  persistedTypOk?: string | null,
): string | null {
  const resolved = resolveTypOkFromMaterials(materials);
  if (resolved.status === 'resolved') return resolved.typOk;
  const persisted = (persistedTypOk ?? '').trim();
  return isDerivedTypOkValue(persisted) ? persisted : null;
}

function rozvadecZafukEntryHasAnyWorkflowData(entry: RozvadecZafukEntry): boolean {
  return (
    entry.vychoziBod === true ||
    isRozvadecZafukKind(entry.kind) ||
    !!rozvadecZafukEntrySourceId(entry) ||
    !!(entry.sourceRozvadecObjectId ?? '').trim() ||
    parseZafukMetaDouble(entry.okMetrazStartM) != null ||
    parseZafukMetaDouble(entry.okMetrazKonecM) != null ||
    !!(entry.typOk ?? '').trim()
  );
}

/** Vynechá prázdné řádky z „+ Přidat záfuk“, když už existuje vyplněná položka (parita Android). */
export function filterRozvadecZafukEntriesForWorkflowValidation(
  entries: RozvadecZafukEntry[],
): RozvadecZafukEntry[] {
  if (rozvadecZafukEntriesHaveVychoziBod(entries)) {
    return [collapseRozvadecZafukEntriesToObjectVychoziBod(entries)];
  }
  if (entries.length <= 1) return entries;
  const filled = entries.filter(rozvadecZafukEntryHasAnyWorkflowData);
  return filled.length > 0 ? filled : entries;
}

/** Minimálně jedna položka a všechny (po odfiltrování prázdných řádků) musí být validní. */
export function areRozvadecZafukEntriesValid(
  entries: RozvadecZafukEntry[] | null | undefined,
  materials?: readonly OkMaterialUsageLike[] | null,
  objectVychoziBod = false,
): boolean {
  if (objectVychoziBod) return true;
  if (!entries?.length) return false;
  const withTypOk = applyTypOkFromMaterialsToZafukEntries(entries, materials);
  const filtered = filterRozvadecZafukEntriesForWorkflowValidation(withTypOk);
  if (!filtered.length) return false;
  if (rozvadecZafukEntriesHaveVychoziBod(filtered)) return true;
  return filtered.every(isRozvadecZafukEntryValid);
}

/** Lidsky čitelný důvod neúspěchu validace (pro API 400). */
export function describeRozvadecZafukEntriesValidationFailure(
  entries: RozvadecZafukEntry[] | null | undefined,
  materials?: readonly OkMaterialUsageLike[] | null,
  objectVychoziBod = false,
): string | null {
  if (objectVychoziBod) return null;
  if (!entries?.length) {
    return 'Před přechodem vyplňte alespoň jednu položku záfuku.';
  }
  const withTypOk = applyTypOkFromMaterialsToZafukEntries(entries, materials);
  const filtered = filterRozvadecZafukEntriesForWorkflowValidation(withTypOk);
  if (!filtered.length) {
    return 'Před přechodem vyplňte alespoň jednu položku záfuku.';
  }
  if (rozvadecZafukEntriesHaveVychoziBod(filtered)) return null;
  for (let i = 0; i < filtered.length; i++) {
    const entry = filtered[i];
    const label = filtered.length > 1 ? `Záfuk ${i + 1}` : 'Záfuk';
    if (!isRozvadecZafukKind(entry.kind)) {
      return `${label}: ${ROZVADEC_ZAFUK_KIND_REQUIRED_MESSAGE}`;
    }
    if (!rozvadecZafukEntrySourceId(entry)) {
      return `${label}: vyberte zdroj záfuku (rozvaděč nebo spojkoviště).`;
    }
    if (isRozvadecZafukMicrotubeKind(entry.kind)) continue;
    const lengthM = rozvadecZafukEntryLengthM(entry);
    if (lengthM == null || !Number.isFinite(lengthM) || lengthM < 0) {
      return `${label}: zadejte platnou metráž OK (start a konec).`;
    }
    const typOk = (entry.typOk ?? '').trim();
    if (!typOk || !isDerivedTypOkValue(typOk)) {
      return OK_MATERIAL_REQUIRED_MESSAGE;
    }
  }
  return null;
}

/** Fail-closed: unknown / missing kind is unset — never guessed as OK. */
function parseEntryKind(raw: unknown): RozvadecZafukKind | null {
  const s = typeof raw === 'string' ? raw.trim() : '';
  return isRozvadecZafukKind(s) ? s : null;
}

function parseEntryId(raw: unknown, index: number): string {
  const s = typeof raw === 'string' ? raw.trim() : '';
  return s || `legacy-zafuk-${index}`;
}

function normalizeEntryFromRaw(raw: unknown, index: number): RozvadecZafukEntry | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const kind = parseEntryKind(o['kind']);
  const vychoziBod = o['vychoziBod'] === true || o['vychoziBod'] === 'true';
  const typOkRaw = typeof o['typOk'] === 'string' ? o['typOk'].trim() : '';
  const typOk =
    kind === 'opticka_vlna' && typOkRaw && isDerivedTypOkValue(typOkRaw) ? typOkRaw : null;
  const rozId =
    (typeof o['sourceRozvadecObjectId'] === 'string' && o['sourceRozvadecObjectId'].trim()) ||
    (typeof o['zafukZRozvadecObjectId'] === 'string' && o['zafukZRozvadecObjectId'].trim()) ||
    null;
  const rozName =
    (typeof o['sourceRozvadecName'] === 'string' && o['sourceRozvadecName'].trim()) ||
    (typeof o['zafukZRozvadecName'] === 'string' && o['zafukZRozvadecName'].trim()) ||
    null;
  const ref = normalizeZafukSourceRef(o);
  const flat = zafukSourceToFlatFields(ref);
  const legacy = zafukSourceToLegacyFields(ref);
  return {
    id: parseEntryId(o['id'], index),
    kind,
    vychoziBod,
    okMetrazStartM: parseZafukMetaDouble(o['okMetrazStartM'] ?? o['zafukOkMetrazStartM']),
    okMetrazKonecM: parseZafukMetaDouble(o['okMetrazKonecM'] ?? o['zafukOkMetrazKonecM']),
    typOk,
    sourceRozvadecObjectId: legacy.zafukZRozvadecObjectId ?? rozId,
    sourceRozvadecName: legacy.zafukZRozvadecName ?? rozName,
    zafukSourceObjectId: flat.zafukSourceObjectId,
    zafukSourceObjectType: flat.zafukSourceObjectType,
    zafukSourceName: flat.zafukSourceName,
  };
}

/** Sestaví jednu položku ze starých top-level sloupců (backward compat). */
export function rozvadecZafukEntryFromLegacyFields(fields: Partial<RozvadecZafukLegacyFields>): RozvadecZafukEntry {
  const start = parseZafukMetaDouble(fields.zafukOkMetrazStartM);
  const konec = parseZafukMetaDouble(fields.zafukOkMetrazKonecM);
  let okMetrazStartM = start;
  let okMetrazKonecM = konec;
  if (okMetrazStartM == null && okMetrazKonecM == null) {
    const len = parseZafukMetaDouble(fields.zafukLengthM);
    if (len != null) {
      okMetrazStartM = 0;
      okMetrazKonecM = len;
    }
  }
  const typOkRaw = (fields.zafukTypOk ?? '').trim();
  const typOk =
    typOkRaw && isDerivedTypOkValue(typOkRaw) ? typOkRaw : null;
  return {
    id: 'legacy-primary',
    kind: 'opticka_vlna',
    vychoziBod: fields.zafukVychoziBod === true,
    okMetrazStartM,
    okMetrazKonecM,
    typOk,
    sourceRozvadecObjectId:
      (fields.zafukZRozvadecObjectId ?? '').trim() || null,
    sourceRozvadecName: (fields.zafukZRozvadecName ?? '').trim() || null,
  };
}

/**
 * Načte pole záfuků z metadata / commit payloadu; při absenci pole vrátí jednu položku ze starých sloupců.
 */
export function normalizeRozvadecZafukEntries(
  sources: {
    rozvadecZafukEntries?: unknown;
    metadata?: Record<string, unknown> | null;
    zafukVychoziBod?: boolean | null;
    /** API/DB may return number or string; normalized via parseZafukMetaDouble. */
    zafukLengthM?: unknown;
    zafukOkMetrazStartM?: unknown;
    zafukOkMetrazKonecM?: unknown;
    zafukTypOk?: string | null;
    zafukZRozvadecObjectId?: string | null;
    zafukZRozvadecName?: string | null;
  },
): RozvadecZafukEntry[] {
  const rawArray = sources.rozvadecZafukEntries ?? sources.metadata?.['rozvadecZafukEntries'];
  if (Array.isArray(rawArray) && rawArray.length > 0) {
    const parsed = rawArray
      .map((item, index) => normalizeEntryFromRaw(item, index))
      .filter((e): e is RozvadecZafukEntry => e != null);
    if (parsed.length > 0) {
      if (
        sources.zafukVychoziBod === true ||
        sources.metadata?.['zafukVychoziBod'] === true ||
        rozvadecZafukEntriesHaveVychoziBod(parsed)
      ) {
        return [collapseRozvadecZafukEntriesToObjectVychoziBod(parsed)];
      }
      return parsed;
    }
  }
  const legacy = rozvadecZafukEntryFromLegacyFields({
    zafukVychoziBod: sources.zafukVychoziBod === true || sources.metadata?.['zafukVychoziBod'] === true,
    zafukLengthM:
      parseZafukMetaDouble(sources.zafukLengthM) ??
      parseZafukMetaDouble(sources.metadata?.['zafukLengthM']),
    zafukOkMetrazStartM:
      parseZafukMetaDouble(sources.zafukOkMetrazStartM) ??
      parseZafukMetaDouble(sources.metadata?.['zafukOkMetrazStartM']),
    zafukOkMetrazKonecM:
      parseZafukMetaDouble(sources.zafukOkMetrazKonecM) ??
      parseZafukMetaDouble(sources.metadata?.['zafukOkMetrazKonecM']),
    zafukTypOk:
      (typeof sources.zafukTypOk === 'string' ? sources.zafukTypOk : null) ??
      (typeof sources.metadata?.['zafukTypOk'] === 'string' ? sources.metadata['zafukTypOk'] : null),
    zafukZRozvadecObjectId:
      (typeof sources.zafukZRozvadecObjectId === 'string' ? sources.zafukZRozvadecObjectId : null) ??
      (typeof sources.metadata?.['zafukZRozvadecObjectId'] === 'string'
        ? sources.metadata['zafukZRozvadecObjectId']
        : null),
    zafukZRozvadecName:
      (typeof sources.zafukZRozvadecName === 'string' ? sources.zafukZRozvadecName : null) ??
      (typeof sources.metadata?.['zafukZRozvadecName'] === 'string'
        ? sources.metadata['zafukZRozvadecName']
        : null),
  });
  const hasLegacyData =
    legacy.vychoziBod ||
    legacy.sourceRozvadecObjectId ||
    rozvadecZafukEntryLengthM(legacy) != null ||
    legacy.typOk;
  return hasLegacyData ? [legacy] : [createEmptyRozvadecZafukEntry()];
}

/** První optická vlákna položka (ne výchozí bod) → legacy sloupce pro starší klienty a BUDOVA vazby. */
export function rozvadecZafukEntriesToLegacyFields(
  entries: RozvadecZafukEntry[],
): RozvadecZafukLegacyFields {
  if (rozvadecZafukEntriesHaveVychoziBod(entries)) {
    return {
      zafukVychoziBod: true,
      zafukLengthM: null,
      zafukOkMetrazStartM: null,
      zafukOkMetrazKonecM: null,
      zafukTypOk: null,
      zafukZRozvadecObjectId: null,
      zafukZRozvadecName: null,
    };
  }
  const primary =
    entries.find((e) => e.kind === 'opticka_vlna' && !e.vychoziBod) ??
    entries.find((e) => e.kind === 'opticka_vlna') ??
    entries[0];
  if (!primary) {
    return {
      zafukVychoziBod: false,
      zafukLengthM: null,
      zafukOkMetrazStartM: null,
      zafukOkMetrazKonecM: null,
      zafukTypOk: null,
      zafukZRozvadecObjectId: null,
      zafukZRozvadecName: null,
    };
  }
  if (primary.vychoziBod) {
    return {
      zafukVychoziBod: true,
      zafukLengthM: null,
      zafukOkMetrazStartM: null,
      zafukOkMetrazKonecM: null,
      zafukTypOk: null,
      zafukZRozvadecObjectId: null,
      zafukZRozvadecName: null,
    };
  }
  return {
    zafukVychoziBod: false,
    zafukLengthM: rozvadecZafukEntryLengthM(primary),
    zafukOkMetrazStartM: primary.okMetrazStartM ?? null,
    zafukOkMetrazKonecM: primary.okMetrazKonecM ?? null,
    zafukTypOk: primary.typOk ?? null,
    zafukZRozvadecObjectId: primary.sourceRozvadecObjectId ?? null,
    zafukZRozvadecName: primary.sourceRozvadecName ?? null,
  };
}

/** Weld-cable lookup refused these persisted záfuk shapes — do not invent ids or fiber caps. */
export const ROZVADEC_ZAFUK_WELD_UNSUPPORTED = {
  NOT_ARRAY: 'rozvadecZafukEntries-not-array',
  MISSING_STABLE_ID: 'optical-zafuk-entry-missing-stable-id',
  DUPLICATE_IDS: 'duplicate-zafuk-entry-ids',
} as const;

export type RozvadecWeldCable = {
  id: string;
  typOk: string | null;
  fiberCount: number | null;
  kind: RozvadecZafukKind;
  /** Upstream cabinet named on this local záfuk entry. Never a foreign entry UUID. */
  sourceRozvadecObjectId?: string | null;
  sourceRozvadecName?: string | null;
};

export type RozvadecWeldCableLookup = {
  cables: readonly RozvadecWeldCable[];
  microtubeIds: readonly string[];
  unsupportedShape: string | null;
};

function canonicalTypOk(raw: unknown): string | null {
  const typOk = typeof raw === 'string' ? raw.trim() : '';
  return typOk && isAnyTypOkValue(typOk) ? typOk : null;
}

function parseSourceRozvadecObjectId(o: Record<string, unknown>): string | null {
  const fromEntry =
    typeof o['sourceRozvadecObjectId'] === 'string' ? o['sourceRozvadecObjectId'].trim() : '';
  const fromLegacy =
    typeof o['zafukZRozvadecObjectId'] === 'string' ? o['zafukZRozvadecObjectId'].trim() : '';
  return fromEntry || fromLegacy || null;
}

function parseSourceRozvadecName(o: Record<string, unknown>): string | null {
  const fromEntry =
    typeof o['sourceRozvadecName'] === 'string' ? o['sourceRozvadecName'].trim() : '';
  const fromLegacy =
    typeof o['zafukZRozvadecName'] === 'string' ? o['zafukZRozvadecName'].trim() : '';
  return fromEntry || fromLegacy || null;
}

export function sourceRozvadecObjectIdForWeldCable(
  lookup: RozvadecWeldCableLookup,
  cableId: string | null | undefined,
): string | null {
  const id = (cableId ?? '').trim();
  if (!id) return null;
  return lookup.cables.find((row) => row.id === id)?.sourceRozvadecObjectId?.trim() || null;
}

/** Prefix for outgoing primary cables mirrored from a peer destination záfuk. */
export const OUTGOING_WELD_CABLE_ID_PREFIX = 'outgoing:';

export function outgoingWeldCableId(destCabinetId: string, incomingCableId: string): string {
  const dest = destCabinetId.trim();
  const cable = incomingCableId.trim();
  return dest && cable ? `${OUTGOING_WELD_CABLE_ID_PREFIX}${dest}:${cable}` : '';
}

export function isOutgoingWeldCableId(cableId: string | null | undefined): boolean {
  return (cableId ?? '').trim().startsWith(OUTGOING_WELD_CABLE_ID_PREFIX);
}

/** Optical cables on a destination cabinet whose „zafouknuto z“ is `sourceCabinetId`. */
export function peerLookupCablesPointingAtSource(
  lookup: RozvadecWeldCableLookup,
  sourceCabinetId: string,
): RozvadecWeldCable[] {
  const source = sourceCabinetId.trim();
  if (!source) return [];
  return lookup.cables.filter((cable) => (cable.sourceRozvadecObjectId ?? '').trim() === source);
}

function emptyWeldCableLookup(unsupportedShape: string | null = null): RozvadecWeldCableLookup {
  return { cables: [], microtubeIds: [], unsupportedShape };
}

function inspectRawZafukArray(raw: readonly unknown[]): RozvadecWeldCableLookup {
  const cables: RozvadecWeldCable[] = [];
  const microtubeIds: string[] = [];
  const seen = new Set<string>();
  let missingStableId = false;
  for (const item of raw) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const o = item as Record<string, unknown>;
    const kind = parseEntryKind(o['kind']);
    const id = typeof o['id'] === 'string' ? o['id'].trim() : '';
    if (!kind) continue;
    if (isRozvadecZafukMicrotubeKind(kind)) {
      if (id) microtubeIds.push(id);
      continue;
    }
    // Unset / unknown kind is not a weld cable — never guessed as opticka_vlna.
    if (kind !== 'opticka_vlna') continue;
    const vychoziBod = o['vychoziBod'] === true || o['vychoziBod'] === 'true';
    // Origin of a blown cable — fibers leave here; they are incoming only at the destination.
    if (vychoziBod) continue;
    if (!id) {
      missingStableId = true;
      continue;
    }
    if (seen.has(id)) {
      return emptyWeldCableLookup(ROZVADEC_ZAFUK_WELD_UNSUPPORTED.DUPLICATE_IDS);
    }
    seen.add(id);
    const typOk = canonicalTypOk(o['typOk'] ?? o['zafukTypOk']);
    cables.push({
      id,
      typOk,
      fiberCount: fiberCountFromTypOk(typOk),
      kind,
      sourceRozvadecObjectId: parseSourceRozvadecObjectId(o),
      sourceRozvadecName: parseSourceRozvadecName(o),
    });
  }
  if (missingStableId) {
    return emptyWeldCableLookup(ROZVADEC_ZAFUK_WELD_UNSUPPORTED.MISSING_STABLE_ID);
  }
  return { cables, microtubeIds, unsupportedShape: null };
}

function inspectLegacyZafukWeldCables(
  sources: Parameters<typeof normalizeRozvadecZafukEntries>[0],
): RozvadecWeldCableLookup {
  const legacy = rozvadecZafukEntryFromLegacyFields({
    zafukVychoziBod: sources.zafukVychoziBod === true || sources.metadata?.['zafukVychoziBod'] === true,
    zafukLengthM:
      parseZafukMetaDouble(sources.zafukLengthM) ??
      parseZafukMetaDouble(sources.metadata?.['zafukLengthM']),
    zafukOkMetrazStartM:
      parseZafukMetaDouble(sources.zafukOkMetrazStartM) ??
      parseZafukMetaDouble(sources.metadata?.['zafukOkMetrazStartM']),
    zafukOkMetrazKonecM:
      parseZafukMetaDouble(sources.zafukOkMetrazKonecM) ??
      parseZafukMetaDouble(sources.metadata?.['zafukOkMetrazKonecM']),
    zafukTypOk:
      (typeof sources.zafukTypOk === 'string' ? sources.zafukTypOk : null) ??
      (typeof sources.metadata?.['zafukTypOk'] === 'string' ? sources.metadata['zafukTypOk'] : null),
    zafukZRozvadecObjectId:
      (typeof sources.zafukZRozvadecObjectId === 'string' ? sources.zafukZRozvadecObjectId : null) ??
      (typeof sources.metadata?.['zafukZRozvadecObjectId'] === 'string'
        ? sources.metadata['zafukZRozvadecObjectId']
        : null),
    zafukZRozvadecName:
      (typeof sources.zafukZRozvadecName === 'string' ? sources.zafukZRozvadecName : null) ??
      (typeof sources.metadata?.['zafukZRozvadecName'] === 'string'
        ? sources.metadata['zafukZRozvadecName']
        : null),
  });
  if (legacy.vychoziBod) return emptyWeldCableLookup();
  const hasLegacyData =
    legacy.sourceRozvadecObjectId ||
    rozvadecZafukEntryLengthM(legacy) != null ||
    legacy.typOk;
  if (!hasLegacyData) return emptyWeldCableLookup();
  const typOk = canonicalTypOk(legacy.typOk);
  return {
    cables: [
      {
        id: 'legacy-primary',
        typOk,
        fiberCount: fiberCountFromTypOk(typOk),
        kind: 'opticka_vlna',
        sourceRozvadecObjectId: legacy.sourceRozvadecObjectId ?? null,
        sourceRozvadecName: legacy.sourceRozvadecName ?? null,
      },
    ],
    microtubeIds: [],
    unsupportedShape: null,
  };
}

/**
 * Server-safe incoming weld cables from persisted ROZVADEC záfuk.
 * Výchozí bod (origin) entries are omitted — those fibers leave this cabinet.
 * Does not invent placeholder UUIDs. Ambiguous shapes set `unsupportedShape`.
 */
export function inspectRozvadecWeldCablesFromZafuk(
  sources: Parameters<typeof normalizeRozvadecZafukEntries>[0],
): RozvadecWeldCableLookup {
  const raw = sources.rozvadecZafukEntries ?? sources.metadata?.['rozvadecZafukEntries'];
  if (raw != null && !Array.isArray(raw)) {
    return emptyWeldCableLookup(ROZVADEC_ZAFUK_WELD_UNSUPPORTED.NOT_ARRAY);
  }
  if (Array.isArray(raw) && raw.length > 0) {
    return inspectRawZafukArray(raw);
  }
  return inspectLegacyZafukWeldCables(sources);
}

/**
 * Optical záfuk that *leaves* this cabinet (výchozí bod). Incoming inspect omits these;
 * the weld editor lists them as outgoing primary fibers at the origin (OS splice).
 * Does not change záfuk commit / single-origin UI — read-only listing.
 */
export function inspectRozvadecOriginWeldCablesFromZafuk(
  sources: Parameters<typeof normalizeRozvadecZafukEntries>[0],
): RozvadecWeldCable[] {
  const raw = sources.rozvadecZafukEntries ?? sources.metadata?.['rozvadecZafukEntries'];
  if (Array.isArray(raw) && raw.length > 0) {
    return originCablesFromRawArray(raw);
  }
  return originCablesFromLegacy(sources);
}

function originCablesFromRawArray(raw: readonly unknown[]): RozvadecWeldCable[] {
  const cables: RozvadecWeldCable[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const o = item as Record<string, unknown>;
    const vychoziBod = o['vychoziBod'] === true || o['vychoziBod'] === 'true';
    if (!vychoziBod) continue;
    const kind = parseEntryKind(o['kind']);
    if (kind && isRozvadecZafukMicrotubeKind(kind)) continue;
    if (kind && kind !== 'opticka_vlna') continue;
    const id = typeof o['id'] === 'string' ? o['id'].trim() : '';
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const typOk = canonicalTypOk(o['typOk'] ?? o['zafukTypOk']);
    cables.push({
      id,
      typOk,
      fiberCount: fiberCountFromTypOk(typOk),
      kind: 'opticka_vlna',
      sourceRozvadecObjectId: parseSourceRozvadecObjectId(o),
      sourceRozvadecName: parseSourceRozvadecName(o),
    });
  }
  return cables;
}

function originCablesFromLegacy(
  sources: Parameters<typeof normalizeRozvadecZafukEntries>[0],
): RozvadecWeldCable[] {
  const flag =
    sources.zafukVychoziBod === true || sources.metadata?.['zafukVychoziBod'] === true;
  if (!flag) return [];
  const typOk = canonicalTypOk(
    (typeof sources.zafukTypOk === 'string' ? sources.zafukTypOk : null) ??
      (typeof sources.metadata?.['zafukTypOk'] === 'string' ? sources.metadata['zafukTypOk'] : null),
  );
  return [
    {
      id: 'legacy-primary',
      typOk,
      fiberCount: fiberCountFromTypOk(typOk),
      kind: 'opticka_vlna',
    },
  ];
}

export const KONCOVY_BOD_ZAFUKU_VYCHOZI_BOD_FORBIDDEN_MESSAGE =
  'Koncový bod záfuku nesmí být výchozí bod.';

export function stripKoncovyBodZafukVychoziBod(entries: RozvadecZafukEntry[]): RozvadecZafukEntry[] {
  return entries.map((entry) =>
    entry.vychoziBod
      ? {
          ...entry,
          vychoziBod: false,
          kind: isRozvadecZafukKind(entry.kind) ? entry.kind : null,
        }
      : entry,
  );
}

export function koncovyBodZafukHasForbiddenVychoziBod(
  entries: RozvadecZafukEntry[] | null | undefined,
): boolean {
  return (entries ?? []).some((entry) => entry.vychoziBod === true);
}

export function areKoncovyBodZafukEntriesValid(
  entries: RozvadecZafukEntry[] | null | undefined,
  materials?: readonly OkMaterialUsageLike[] | null,
): boolean {
  if (koncovyBodZafukHasForbiddenVychoziBod(entries)) return false;
  return areRozvadecZafukEntriesValid(entries, materials);
}

export function describeKoncovyBodZafukEntriesValidationFailure(
  entries: RozvadecZafukEntry[] | null | undefined,
  materials?: readonly OkMaterialUsageLike[] | null,
): string | null {
  if (koncovyBodZafukHasForbiddenVychoziBod(entries)) {
    return KONCOVY_BOD_ZAFUKU_VYCHOZI_BOD_FORBIDDEN_MESSAGE;
  }
  return describeRozvadecZafukEntriesValidationFailure(entries, materials);
}

export function serializeKoncovyBodZafukEntriesForCommit(
  entries: RozvadecZafukEntry[],
): RozvadecZafukEntry[] {
  return serializeRozvadecZafukEntriesForCommit(stripKoncovyBodZafukVychoziBod(entries)).map(
    (entry) => ({ ...entry, vychoziBod: false }),
  );
}

export function serializeRozvadecZafukEntriesForCommit(
  entries: RozvadecZafukEntry[],
): RozvadecZafukEntry[] {
  if (rozvadecZafukEntriesHaveVychoziBod(entries)) {
    return [collapseRozvadecZafukEntriesToObjectVychoziBod(entries)];
  }
  return entries.map((e) => {
    const ref = e.vychoziBod ? null : normalizeZafukSourceRef(e);
    const flat = zafukSourceToFlatFields(ref);
    const legacy = zafukSourceToLegacyFields(ref);
    return {
      id: e.id,
      kind: e.vychoziBod ? 'opticka_vlna' : isRozvadecZafukKind(e.kind) ? e.kind : null,
      vychoziBod: e.vychoziBod === true,
      okMetrazStartM: e.vychoziBod ? null : (parseZafukMetaDouble(e.okMetrazStartM) ?? null),
      okMetrazKonecM: e.vychoziBod ? null : (parseZafukMetaDouble(e.okMetrazKonecM) ?? null),
      typOk:
        e.vychoziBod || e.kind !== 'opticka_vlna'
          ? null
          : (e.typOk?.trim() && isDerivedTypOkValue(e.typOk.trim()) ? e.typOk.trim() : null),
      sourceRozvadecObjectId: e.vychoziBod ? null : legacy.zafukZRozvadecObjectId,
      sourceRozvadecName: e.vychoziBod ? null : legacy.zafukZRozvadecName,
      zafukSourceObjectId: e.vychoziBod ? null : flat.zafukSourceObjectId,
      zafukSourceObjectType: e.vychoziBod ? null : flat.zafukSourceObjectType,
      zafukSourceName: e.vychoziBod ? null : flat.zafukSourceName,
    };
  });
}

/** Informace — jeden řádek label / hodnota (parita popup Informace). */
export type RozvadecZafukInfoRow = { label: string; value: string };

/** Informace — blok jednoho záfuku (vizuálně oddělený při více položkách). */
export type RozvadecZafukInfoBlock = {
  /** `null` = jedna položka → sekce „Záfuk“; jinak „Záfuk 1“, „Záfuk 2“, … */
  title: string | null;
  rows: RozvadecZafukInfoRow[];
};

/**
 * Informace modal / bottom sheet — strukturované bloky (label/value).
 * Více záfuků = oddělené sekce Záfuk 1 / Záfuk 2.
 */
export function buildRozvadecZafukInfoBlocks(entries: RozvadecZafukEntry[]): RozvadecZafukInfoBlock[] {
  const filtered = filterRozvadecZafukEntriesForWorkflowValidation(entries);
  if (filtered.length === 0) return [];
  if (rozvadecZafukEntriesHaveVychoziBod(filtered)) {
    return [{ title: null, rows: [{ label: 'Typ', value: ROZVADEC_ZAFUK_VYCHOZI_BOD_LABEL }] }];
  }
  return filtered.map((entry, index) =>
    buildSingleRozvadecZafukInfoBlock(entry, index, filtered.length),
  );
}

function buildSingleRozvadecZafukInfoBlock(
  entry: RozvadecZafukEntry,
  index: number,
  total: number,
): RozvadecZafukInfoBlock {
  if (entry.vychoziBod) {
    return {
      title: null,
      rows: [{ label: 'Typ', value: ROZVADEC_ZAFUK_VYCHOZI_BOD_LABEL }],
    };
  }

  const kindLabel = rozvadecZafukKindLabel(entry.kind);
  const title = total > 1 ? `Záfuk ${index + 1} (${kindLabel})` : null;

  const rows: RozvadecZafukInfoRow[] = [];
  if (entry.kind === 'opticka_vlna') {
    const typOk = (entry.typOk ?? '').trim();
    if (typOk && isDerivedTypOkValue(typOk)) {
      rows.push({ label: 'Typ OK', value: typOk });
    }
    const start = parseZafukMetaDouble(entry.okMetrazStartM);
    if (start != null && start >= 0) {
      rows.push({ label: 'Metráž startu OK', value: `${formatZafukReviewM(start)} m` });
    }
    const end = parseZafukMetaDouble(entry.okMetrazKonecM);
    if (end != null && end >= 0) {
      rows.push({ label: 'Metráž na konci OK', value: `${formatZafukReviewM(end)} m` });
    }
    const lengthM = rozvadecZafukEntryLengthM(entry);
    if (lengthM != null && lengthM >= 0) {
      rows.push({ label: 'Délka záfuku', value: `${formatZafukReviewM(lengthM)} m` });
    }
  } else if (total === 1) {
    rows.push({ label: 'Druh', value: kindLabel });
  }
  const ref = normalizeZafukSourceRef(entry);
  const stored = (ref?.name ?? entry.sourceRozvadecName ?? '').trim();
  const rozId = (ref?.objectId ?? entry.sourceRozvadecObjectId ?? '').trim();
  const sourceLabel = stored && stored !== rozId ? stored : stored || rozId || '';
  if (sourceLabel) rows.push({ label: 'Zafouknuto z', value: sourceLabel });
  return { title, rows };
}

/** Informace / review — flat řádky (phase-confirm); preferuj `buildRozvadecZafukInfoBlocks` v Informace. */
export function buildRozvadecZafukInfoReviewLines(entries: RozvadecZafukEntry[]): string[] {
  return buildRozvadecZafukInfoBlocks(entries).flatMap((block) => {
    const out: string[] = [];
    if (block.title) out.push(block.title);
    for (const row of block.rows) {
      out.push(`${row.label}: ${row.value}`);
    }
    return out;
  });
}

function formatZafukReviewM(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

export interface RozvadecZafukWorkReportMeters {
  /** Součet |konec − start| u položek primárního OK (ne výchozí bod). */
  primarniOkM: number;
  /** Součet |konec − start| u položek svazku mikrotrubiček (ne výchozí bod). */
  mikrotrubickyM: number;
}

/**
 * Metráž pro výkaz práce na ROZVADEC Zafouknutý: součet |konec − start| podle druhu.
 * Výchozí bod a položky bez platné metráže se nepočítají. Nezahrnuje material_usage.
 */
export function sumRozvadecZafukWorkReportMeters(
  sources: Parameters<typeof normalizeRozvadecZafukEntries>[0],
): RozvadecZafukWorkReportMeters {
  const entries = filterRozvadecZafukEntriesForWorkflowValidation(normalizeRozvadecZafukEntries(sources));
  let primarniOkM = 0;
  let mikrotrubickyM = 0;
  if (rozvadecZafukEntriesHaveVychoziBod(entries)) {
    return { primarniOkM: 0, mikrotrubickyM: 0 };
  }
  for (const entry of entries) {
    const lengthM = rozvadecZafukEntryLengthM(entry);
    if (lengthM == null || !Number.isFinite(lengthM) || lengthM <= 0) continue;
    if (isRozvadecZafukMicrotubeKind(entry.kind)) mikrotrubickyM += lengthM;
    else if (entry.kind === 'opticka_vlna') primarniOkM += lengthM;
  }
  return {
    primarniOkM: Math.round(primarniOkM * 100) / 100,
    mikrotrubickyM: Math.round(mikrotrubickyM * 100) / 100,
  };
}

/**
 * Počet kusů „Záfuk primárního OK“: jedna položka optického vlákna s platnou metráží = 1 ks.
 * Výchozí bod a položky bez metráže se nepočítají. Délka v metrech do ceny nevstupuje.
 */
export function countRozvadecZafukPrimarniOkPieces(
  sources: Parameters<typeof normalizeRozvadecZafukEntries>[0],
): number {
  const entries = filterRozvadecZafukEntriesForWorkflowValidation(normalizeRozvadecZafukEntries(sources));
  if (rozvadecZafukEntriesHaveVychoziBod(entries)) return 0;
  let pieces = 0;
  for (const entry of entries) {
    if (entry.kind !== 'opticka_vlna') continue;
    const lengthM = rozvadecZafukEntryLengthM(entry);
    if (lengthM == null || !Number.isFinite(lengthM) || lengthM <= 0) continue;
    pieces += 1;
  }
  return pieces;
}
